/**
 * An Ask Led answer, rendered.
 *
 * The console used to interpolate `{answer.answer}` directly, which printed
 * the model's `**bold**` markers and its `[R1]` citations as literal text.
 * This is the ONE place an answer string becomes DOM — see
 * `webapp/tests/guards/askAnswerNotRaw.test.ts`, which fails the build if a
 * second one appears.
 */

import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import type { ElementContent, Root } from "hast";
import { CITE_LABEL_UNKNOWN } from "../chrome";
import type { AskReference } from "../../api";
import { C } from "../../tokens";
import {
  CITE_BRACKET,
  citationIds,
  citationLabels,
  citedInOrder,
  stripUnknownCitations,
} from "./askCitations";
import { CiteChip } from "./CiteChip";
import { citationTarget, type CitationTarget } from "./citationTarget";

/**
 * The answer's own type metrics.
 *
 * Exported because the mark beside the answer has to sit on its FIRST LINE,
 * which means knowing how tall that line is. A 22px badge centred in a
 * 23.25px box lands on the text; the same badge centred in the whole answer
 * block drifts to wherever the middle of the paragraph happens to be — which
 * is what it did, ending up level with "311 records read" (user, 2026-09-10).
 *
 * One source, so the mark follows if the answer's scale ever changes.
 */
export const ANSWER_FONT_SIZE = 17.25;
export const ANSWER_LINE_HEIGHT = 1.55;

export function AnswerBody({
  text,
  references,
  onOpen,
  hrefFor,
  testId = "ask-answer",
  fontSize = ANSWER_FONT_SIZE,
}: {
  text: string;
  references: AskReference[];
  /** Omit to render chips as labels rather than controls — the Ask bubble's
   *  documented reason for staying inert. */
  onOpen?: (target: CitationTarget) => void;
  /** Builds the console URL for a citation's destination, from its own
   *  `hrefForItem`/`hrefForWalk` (`AskLed` supplies this from its
   *  `ConsoleViewProps`). Omit — as the Ask bubble does — to keep every chip
   *  a `<span>` even when `onOpen` is supplied: `CiteChip` never renders an
   *  anchor without a real `href`. */
  hrefFor?: (target: CitationTarget) => string;
  testId?: string;
  fontSize?: number;
}) {
  // Unresolvable markers go FIRST, so the whitespace they strand goes with
  // them. Everything below reads the healed text.
  const clean = stripUnknownCitations(text, references);
  const cited = citedInOrder(clean, references);
  const labels = citationLabels(cited);
  const byId = new Map(cited.map((reference) => [reference.id, reference]));

  return (
    <div
      data-testid={testId}
      className="ask-arrive ask-answer-markdown"
      style={{ fontSize, lineHeight: ANSWER_LINE_HEIGHT, color: C.ink }}
    >
      <Markdown
        skipHtml
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeCitations]}
        components={{
          table: ({ children }) => <div className="ask-answer-table" role="region" aria-label="Answer table" tabIndex={0}><table>{children}</table></div>,
          strong: ({ children }) => <b>{children}</b>,
          // Model output cannot load remote images into a private workspace.
          img: ({ alt }) => <span>{alt}</span>,
          a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
          span: ({ node, children }) => {
            const id = node?.properties.dataAskCitation;
            const reference = typeof id === "string" ? byId.get(id) : undefined;
            if (!reference) return <span>{children}</span>;
            const target = citationTarget(reference);
            return (
              <CiteChip
                recordKind={reference.recordKind}
                label={labels[reference.id] ?? CITE_LABEL_UNKNOWN}
                target={target}
                href={target && hrefFor ? hrefFor(target) : undefined}
                onOpen={onOpen}
              />
            );
          },
        }}
      >
        {clean}
      </Markdown>
    </div>
  );
}

/** Replace citation text after Markdown parsing, including inside emphasis
 * and nested lists. Never create a citation link inside another link or code.
 * Raw HTML is not parsed, so only this plugin can create citation spans. */
function rehypeCitations() {
  return (tree: Root) => {
    function visit(parent: Root | Extract<ElementContent, { type: "element" }>) {
      parent.children = parent.children.flatMap((child): ElementContent[] => {
        if (child.type === "element") {
          if (!["a", "code", "pre"].includes(child.tagName)) visit(child);
          return [child];
        }
        if (child.type !== "text") return child.type === "doctype" ? [] : [child];
        const parts: ElementContent[] = [];
        let last = 0;
        for (const match of child.value.matchAll(new RegExp(CITE_BRACKET, "g"))) {
          if (match.index > last) parts.push({ type: "text", value: child.value.slice(last, match.index) });
          for (const id of citationIds(match[1]!)) {
            parts.push({ type: "element", tagName: "span", properties: { dataAskCitation: id }, children: [] });
          }
          last = match.index + match[0].length;
        }
        if (last < child.value.length) parts.push({ type: "text", value: child.value.slice(last) });
        return parts;
      });
    }
    visit(tree);
  };
}
